import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Paper, Tabs, Tab, Select, MenuItem, FormControl,
  InputLabel, Chip, CircularProgress, Alert, IconButton, Tooltip,
  useTheme, alpha,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DashboardIcon from '@mui/icons-material/Dashboard';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import SettingsIcon from '@mui/icons-material/Settings';
import HistoryIcon from '@mui/icons-material/History';
import ScienceIcon from '@mui/icons-material/Science';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import StrategyEngineService from '../../services/StrategyEngineService';
import { BUILT_IN_MODES } from '../../config/tradingPresets';
import ModeSwitcher from './components/ModeSwitcher';
import OverviewTab from './components/OverviewTab';
import PerformanceTab from './components/PerformanceTab';
import SignalsGatesTab from './components/SignalsGatesTab';
import PositionsTab from './components/PositionsTab';
import SettingsTab from './components/SettingsTab';
import AuditLogTab from './components/AuditLogTab';
import ExperienceTab from './components/ExperienceTab';
import AIExperimentsTab from './components/AIExperimentsTab';

const STRATEGY_ENGINE_URL =
  process.env.REACT_APP_STRATEGY_ENGINE_URL ||
  'https://strategy-engine.houman-ghavamzadeh.workers.dev';

const TAB_CONFIG = [
  { label: 'Overview', icon: <DashboardIcon fontSize="small" /> },
  { label: 'Performance', icon: <ShowChartIcon fontSize="small" /> },
  { label: 'Signals & Gates', icon: <FilterAltIcon fontSize="small" /> },
  { label: 'Positions', icon: <AccountBalanceWalletIcon fontSize="small" /> },
  { label: 'Settings', icon: <SettingsIcon fontSize="small" /> },
  { label: 'ML / Experiments', icon: <ScienceIcon fontSize="small" /> },
  { label: 'AI Schedule', icon: <SmartToyIcon fontSize="small" /> },
  { label: 'Audit Log', icon: <HistoryIcon fontSize="small" /> },
];

export default function StrategyDashboard() {
  const theme = useTheme();
  const [activeTab, setActiveTab] = useState(0);
  const [strategies, setStrategies] = useState([]);
  const [selectedStrategyId, setSelectedStrategyId] = useState('');
  const [strategyData, setStrategyData] = useState(null);
  const [scheduleData, setScheduleData] = useState(null);
  const [rejectionData, setRejectionData] = useState(null);
  const [positionsData, setPositionsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  // Derive mode info from schedule
  const modeInfo = useMemo(() => {
    if (!scheduleData) return null;
    const modeId = scheduleData.modeId || 'custom';
    const mode = BUILT_IN_MODES.find(m => m.id === modeId);
    return {
      modeId,
      mode,
      intent: mode?.intent || 'custom',
      isMode1: mode?.intent === 'many_small_wins',
      isMode2: mode?.intent === 'fewer_larger_wins' || mode?.intent === 'pro_selective',
      leverage: scheduleData.leverage || 5,
      profileName: mode?.shortName || 'Custom',
    };
  }, [scheduleData]);

  // Load strategies list
  useEffect(() => {
    const loadStrategies = async () => {
      try {
        const url = `${STRATEGY_ENGINE_URL}/api/strategies`;
        const res = await fetch(url, {
          headers: { 'X-User-Id': localStorage.getItem('user_id') || 'default-user' },
        });
        const data = await res.json();
        const list = Array.isArray(data) ? data : (data.data || []);
        setStrategies(list);
        if (list.length > 0 && !selectedStrategyId) {
          setSelectedStrategyId(list[0].id);
        }
      } catch (err) {
        setError('Failed to load strategies');
      } finally {
        setLoading(false);
      }
    };
    loadStrategies();
  }, []);

  // Load strategy data when selection changes
  const loadStrategyData = useCallback(async (strategyId) => {
    if (!strategyId) return;
    setRefreshing(true);
    setError(null);
    try {
      const headers = { 'X-User-Id': localStorage.getItem('user_id') || 'default-user' };
      const [strategy, schedule, rejections, positions] = await Promise.all([
        StrategyEngineService.getStrategy(strategyId),
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/strategy/${strategyId}/schedule`, { headers })
          .then(r => r.json()).then(d => d.data || d).catch(() => null),
        fetch(`${STRATEGY_ENGINE_URL}/api/debug/rejections/${strategyId}`, { headers })
          .then(r => r.json()).catch(() => null),
        fetch(`${STRATEGY_ENGINE_URL}/api/admin/strategy/${strategyId}/positions`, { headers })
          .then(r => r.json()).then(d => d.data || d || []).catch(() => []),
      ]);
      setStrategyData(strategy);
      setScheduleData(schedule);
      setRejectionData(rejections);
      setPositionsData(Array.isArray(positions) ? positions : []);
    } catch (err) {
      setError('Failed to load strategy data');
    } finally {
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (selectedStrategyId) {
      loadStrategyData(selectedStrategyId);
    }
  }, [selectedStrategyId, loadStrategyData]);

  // Auto-refresh every 60s
  useEffect(() => {
    if (!selectedStrategyId) return;
    const interval = setInterval(() => loadStrategyData(selectedStrategyId), 60000);
    return () => clearInterval(interval);
  }, [selectedStrategyId, loadStrategyData]);

  const handleRefresh = () => loadStrategyData(selectedStrategyId);

  const handleModeChange = async (newModeId, newProfile) => {
    // Apply mode change to schedule via API
    try {
      const mode = BUILT_IN_MODES.find(m => m.id === newModeId);
      if (!mode) return;
      await StrategyEngineService.updateStrategy(selectedStrategyId, {
        ...strategyData,
        settings: { ...strategyData?.settings, ...mode.params, modeId: newModeId, modeName: mode.name },
      });
      await loadStrategyData(selectedStrategyId);
    } catch (err) {
      setError('Failed to apply mode change');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const selectedStrategy = strategies.find(s => s.id === selectedStrategyId);

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', px: { xs: 1, md: 3 }, py: 2 }}>
      {/* Header: Strategy Selector + Refresh */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap',
        p: 2, borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(11,17,32,0.85) 0%, rgba(24,27,33,0.9) 100%)',
        backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
        border: '1px solid rgba(255,255,255,0.06)',
      }}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Strategy</InputLabel>
          <Select
            value={selectedStrategyId}
            onChange={(e) => setSelectedStrategyId(e.target.value)}
            label="Strategy"
          >
            {strategies.map(s => (
              <MenuItem key={s.id} value={s.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{
                    width: 8, height: 8, borderRadius: '50%',
                    bgcolor: s.isActive ? 'success.main' : 'text.disabled',
                  }} />
                  <Typography variant="body2">{s.name}</Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {modeInfo && (
          <Chip
            label={modeInfo.profileName}
            color={modeInfo.isMode1 ? 'warning' : modeInfo.isMode2 ? 'info' : 'default'}
            size="small"
            variant="outlined"
          />
        )}

        {selectedStrategy?.isActive && (
          <Chip label="Running" color="success" size="small" variant="filled" />
        )}

        <Box sx={{ flex: 1 }} />

        <Tooltip title="Refresh data">
          <IconButton onClick={handleRefresh} disabled={refreshing} size="small">
            <RefreshIcon sx={{ animation: refreshing ? 'spin 1s linear infinite' : 'none' }} />
          </IconButton>
        </Tooltip>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Mode Switcher */}
      <ModeSwitcher
        modeInfo={modeInfo}
        scheduleData={scheduleData}
        onModeChange={handleModeChange}
      />

      {/* Tab Navigation */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: 2,
          bgcolor: 'transparent',
        }}
      >
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              minHeight: 48,
              fontWeight: 500,
              fontSize: '0.875rem',
              gap: 0.5,
            },
          }}
        >
          {TAB_CONFIG.map((tab, i) => (
            <Tab key={i} label={tab.label} icon={tab.icon} iconPosition="start" />
          ))}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box sx={{ minHeight: 400 }}>
        {activeTab === 0 && (
          <OverviewTab
            strategyId={selectedStrategyId}
            strategyData={strategyData}
            scheduleData={scheduleData}
            rejectionData={rejectionData}
            positionsData={positionsData}
            modeInfo={modeInfo}
            onRefresh={handleRefresh}
          />
        )}
        {activeTab === 1 && (
          <PerformanceTab
            strategyId={selectedStrategyId}
            positionsData={positionsData}
            scheduleData={scheduleData}
            modeInfo={modeInfo}
          />
        )}
        {activeTab === 2 && (
          <SignalsGatesTab
            strategyId={selectedStrategyId}
            rejectionData={rejectionData}
            scheduleData={scheduleData}
            modeInfo={modeInfo}
            onRefresh={handleRefresh}
          />
        )}
        {activeTab === 3 && (
          <PositionsTab
            strategyId={selectedStrategyId}
            positionsData={positionsData}
            onRefresh={handleRefresh}
          />
        )}
        {activeTab === 4 && (
          <SettingsTab
            strategyId={selectedStrategyId}
            strategyData={strategyData}
            scheduleData={scheduleData}
            modeInfo={modeInfo}
            strategies={strategies}
            onRefresh={handleRefresh}
          />
        )}
        {activeTab === 5 && (
          <ExperienceTab
            strategyId={selectedStrategyId}
            scheduleData={scheduleData}
          />
        )}
        {activeTab === 6 && (
          <AIExperimentsTab
            strategyId={selectedStrategyId}
            scheduleData={scheduleData}
          />
        )}
        {activeTab === 7 && (
          <AuditLogTab
            strategyId={selectedStrategyId}
          />
        )}
      </Box>

      {/* CSS for spinner animation */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}
